import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserInfo } from '../services/oidcService';
import { SYBERKEY_KEY_IMG, SYBERKEY_WORDMARK_IMG } from '../branding';
import SyberKeyInlineMark from '../components/SyberKeyInlineMark';
import './Dashboard.css';

// Available services/apps
// Use production URLs in production, localhost for development
const isProduction = typeof window !== 'undefined' && 
  (window.location.hostname === 'sso.syberkey.com' || 
   !window.location.hostname.includes('localhost'));

const availableServices = [
  { 
    name: 'Accounts', 
    icon: '💰', 
    url: isProduction ? 'https://accounts.syberkey.com' : 'http://localhost:3004',
    description: 'Financial management and accounting',
    color: 'cyan'
  },
  { 
    name: 'HR', 
    icon: '👥', 
    url: isProduction ? 'https://hr.syberkey.com' : 'http://localhost:3005',
    description: 'Human resources and employee management',
    color: 'yellow'
  },
  { 
    name: 'Sales', 
    icon: '💼', 
    url: isProduction ? 'https://sales.syberkey.com' : 'http://localhost:3006',
    description: 'Sales pipeline and customer management',
    color: 'cyan'
  },
];

// Dummy data for dashboard
const dummyStats = [
  { label: 'Active Projects', value: '12', icon: '📊', color: 'cyan' },
  { label: 'Team Members', value: '48', icon: '👥', color: 'yellow' },
  { label: 'Tasks Completed', value: '234', icon: '✅', color: 'cyan' },
  { label: 'Pending Reviews', value: '8', icon: '📝', color: 'yellow' },
];

const dummyProjects = [
  { id: 1, name: 'Q1 Product Launch', status: 'In Progress', progress: 75, team: 12 },
  { id: 2, name: 'Security Audit', status: 'Review', progress: 90, team: 8 },
  { id: 3, name: 'Mobile App Update', status: 'Planning', progress: 30, team: 15 },
  { id: 4, name: 'Infrastructure Upgrade', status: 'In Progress', progress: 60, team: 10 },
];

const dummyRecentActivity = [
  { id: 1, action: 'Completed task', item: 'Q1 Product Launch - Design Review', time: '2 hours ago' },
  { id: 2, action: 'Added comment', item: 'Security Audit - Penetration Testing', time: '5 hours ago' },
  { id: 3, action: 'Updated status', item: 'Mobile App Update - Beta Testing', time: '1 day ago' },
  { id: 4, action: 'Assigned task', item: 'Infrastructure Upgrade - Server Migration', time: '2 days ago' },
];

const Dashboard: React.FC = () => {
  const { user, logout, isAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();
  const [displayUser, setDisplayUser] = useState<any>(null);
  const [isFetchingUserInfo, setIsFetchingUserInfo] = useState(false);
  const [isAppLauncherOpen, setIsAppLauncherOpen] = useState(false);
  
  // Guard to prevent duplicate /oidc/me API calls
  const hasFetchedUserInfoRef = useRef(false);
  const appLauncherRef = useRef<HTMLDivElement>(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');
      
      if (!savedToken || !savedUser) {
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  // useEffect to handle user state updates and fetch missing user info
  useEffect(() => {
    const loadAndEnhanceUserData = async () => {
      let currentUser = user;
      
      if (!currentUser) {
        try {
          const savedUser = localStorage.getItem('auth_user');
          currentUser = savedUser ? JSON.parse(savedUser) : null;
        } catch (error) {
          console.error('[Dashboard] Error parsing user from localStorage:', error);
          setDisplayUser(null);
          return;
        }
      }
      
      if (!currentUser) {
        setDisplayUser(null);
        return;
      }
      
      const missingEmail = !currentUser.email || currentUser.email === 'Not available';
      const missingFullName = !currentUser.fullName || currentUser.fullName === 'Not available';
      
      if ((missingEmail || missingFullName) && !isFetchingUserInfo && !hasFetchedUserInfoRef.current) {
        const accessToken = localStorage.getItem('auth_token');
        if (accessToken && currentUser.userId) {
          setIsFetchingUserInfo(true);
          hasFetchedUserInfoRef.current = true;
          
          try {
            const userInfo = await getUserInfo(accessToken);
            
            const enhancedUser = {
              ...currentUser,
              email: currentUser.email && currentUser.email !== 'Not available' 
                ? currentUser.email 
                : (userInfo.email || 'Not available'),
              fullName: currentUser.fullName && currentUser.fullName !== 'Not available'
                ? currentUser.fullName
                : (userInfo.name || userInfo.preferred_username || 'Not available')
            };
            
            localStorage.setItem('auth_user', JSON.stringify(enhancedUser));
            setUser(enhancedUser);
            setDisplayUser(enhancedUser);
          } catch (error) {
            console.error('[Dashboard] ❌ Error fetching user info:', error);
            hasFetchedUserInfoRef.current = false;
            setDisplayUser(currentUser);
          } finally {
            setIsFetchingUserInfo(false);
          }
        } else {
          setDisplayUser(currentUser);
        }
      } else {
        setDisplayUser(currentUser);
      }
    };
    
    loadAndEnhanceUserData();
  }, [user, setUser, isFetchingUserInfo]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Dashboard: Logout failed:', error);
    }
  };

  // Close app launcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appLauncherRef.current && !appLauncherRef.current.contains(event.target as Node)) {
        setIsAppLauncherOpen(false);
      }
    };

    if (isAppLauncherOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAppLauncherOpen]);

  return (
    <div className="dashboard-container">
      {/* Navbar */}
      <nav className="dashboard-navbar">
        <div className="dashboard-navbar-container">
          <Link to="/" className="dashboard-navbar-brand">
            <img src={SYBERKEY_KEY_IMG} alt="" className="dashboard-logo-img" />
            <img src={SYBERKEY_WORDMARK_IMG} alt="SyberKey" className="dashboard-wordmark-img" />
          </Link>
          <div className="dashboard-navbar-nav">
            <Link to="/" className="nav-link">Home</Link>
            
            {/* App Launcher */}
            <div className="app-launcher-wrapper" ref={appLauncherRef}>
              <button
                className="app-launcher-button"
                onClick={() => setIsAppLauncherOpen(!isAppLauncherOpen)}
                aria-label="SyberKey apps"
                aria-expanded={isAppLauncherOpen}
              >
                <svg className="app-launcher-icon" viewBox="0 0 24 24" focusable="false">
                  <path d="M6,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM6,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM6,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2z"></path>
                </svg>
              </button>
              
              {isAppLauncherOpen && (
                <div className="app-launcher-dropdown">
                  <div className="app-launcher-header">
                    <span>
                      <SyberKeyInlineMark /> Apps
                    </span>
                  </div>
                  <div className="app-launcher-grid">
                    {availableServices.map((service, index) => {
                      // Get SSO token from localStorage to pass to the app
                      let ssoToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
                      
                      // Validate token format - must be a JWT (3 parts separated by dots)
                      if (ssoToken) {
                        const tokenParts = ssoToken.split('.');
                        const isValidJWT = tokenParts.length === 3 && tokenParts.every(part => part.length > 0);
                        
                        console.log('[Dashboard App Launcher] SSO token found');
                        console.log('[Dashboard App Launcher] Token length:', ssoToken.length);
                        console.log('[Dashboard App Launcher] Token preview:', ssoToken.substring(0, 30));
                        console.log('[Dashboard App Launcher] Token parts:', tokenParts.length);
                        console.log('[Dashboard App Launcher] Is valid JWT:', isValidJWT);
                        
                        if (!isValidJWT) {
                          console.error('[Dashboard App Launcher] ERROR: Invalid token format - not a JWT!');
                          console.error('[Dashboard App Launcher] Clearing invalid token from localStorage');
                          localStorage.removeItem('sso_token');
                          localStorage.removeItem('auth_token');
                          ssoToken = null;
                        }
                      } else {
                        console.warn('[Dashboard App Launcher] No SSO token found in localStorage!');
                      }
                      
                      const serviceUrl = ssoToken 
                        ? `${service.url}?sso_token=${encodeURIComponent(ssoToken)}`
                        : service.url;
                      
                      return (
                        <a
                          key={index}
                          href={serviceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="app-launcher-item"
                          onClick={() => setIsAppLauncherOpen(false)}
                        >
                          <div className="app-launcher-item-icon">
                            <span>{service.icon}</span>
                          </div>
                          <div className="app-launcher-item-name">{service.name}</div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <button className="btn-yellow" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="dashboard-wrapper">
        <div className="dashboard-content">
          {/* Welcome Section */}
          <div className="dashboard-welcome">
            <h1>Welcome back, {displayUser?.fullName || 'Employee'}!</h1>
            <p>Here's what's happening at Syberkey Pvt Ltd today.</p>
          </div>

          {/* Stats Grid */}
          <div className="dashboard-stats">
            {dummyStats.map((stat, index) => (
              <div key={index} className="stat-card card-syber">
                <div className={`icon-bubble ${stat.color}`}>
                  <span>{stat.icon}</span>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="dashboard-grid">
            {/* Projects Section */}
            <div className="dashboard-section card-syber">
              <div className="section-header">
                <h2>Active Projects</h2>
                <button className="btn-outline-cyan btn-sm">View All</button>
              </div>
              <div className="projects-list">
                {dummyProjects.map((project) => (
                  <div key={project.id} className="project-item">
                    <div className="project-header">
                      <h3>{project.name}</h3>
                      <span className={`status-badge status-${project.status.toLowerCase().replace(' ', '-')}`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="project-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{project.progress}%</span>
                    </div>
                    <div className="project-meta">
                      <span>👥 {project.team} team members</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity Section */}
            <div className="dashboard-section card-syber">
              <div className="section-header">
                <h2>Recent Activity</h2>
                <button className="btn-outline-cyan btn-sm">View All</button>
              </div>
              <div className="activity-list">
                {dummyRecentActivity.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">
                      <span>🔔</span>
                    </div>
                    <div className="activity-content">
                      <div className="activity-text">
                        <strong>{activity.action}</strong> on {activity.item}
                      </div>
                      <div className="activity-time">{activity.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Info Section */}
            <div className="dashboard-section card-syber">
              <div className="section-header">
                <h2>Your Profile</h2>
              </div>
              {displayUser ? (
                <div className="user-profile">
                  <div className="profile-avatar">
                    <span>{displayUser.fullName?.charAt(0) || 'U'}</span>
                  </div>
                  <div className="profile-info">
                    <div className="profile-name">{displayUser.fullName || 'Employee'}</div>
                    <div className="profile-email">{displayUser.email || 'Not available'}</div>
                    <div className="profile-id">ID: {displayUser.userId || 'Not available'}</div>
                  </div>
                  <div className="profile-badges">
                    <span className="badge-soft">🔐 SSO Authenticated</span>
                    <span className="badge-soft">✅ Verified</span>
                  </div>
                </div>
              ) : (
                <div className="user-profile">
                  <div className="profile-loading">Loading user data...</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
